import type { AggregateRow, DistributionRow, MonthRow, OfficerRow } from "./types";

// Two-tailed p-value for Student's t via the regularized incomplete beta
// function (Numerical Recipes continued fraction).
export function tTwoTailedP(t: number, df: number): number {
  if (!Number.isFinite(t) || df <= 0) return 1;
  const x = df / (df + t * t);
  return Math.min(1, Math.max(0, incompleteBeta(x, df / 2, 0.5)));
}

function incompleteBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const lnFront =
    logGamma(a + b) - logGamma(a) - logGamma(b) + a * Math.log(x) + b * Math.log(1 - x);
  const front = Math.exp(lnFront);
  if (x < (a + 1) / (a + b + 2)) return (front * betaCf(x, a, b)) / a;
  return 1 - (front * betaCf(1 - x, b, a)) / b;
}

function betaCf(x: number, a: number, b: number): number {
  const EPS = 1e-12;
  const TINY = 1e-300;
  let c = 1;
  let d = 1 - ((a + b) * x) / (a + 1);
  if (Math.abs(d) < TINY) d = TINY;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= 200; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((a + m2 - 1) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < TINY) d = TINY;
    c = 1 + aa / c;
    if (Math.abs(c) < TINY) c = TINY;
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (a + b + m) * x) / ((a + m2) * (a + m2 + 1));
    d = 1 + aa * d;
    if (Math.abs(d) < TINY) d = TINY;
    c = 1 + aa / c;
    if (Math.abs(c) < TINY) c = TINY;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < EPS) break;
  }
  return h;
}

function logGamma(z: number): number {
  const g = 7;
  const coef = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61503916999185, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
    1.5056327351493116e-7,
  ];
  if (z < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * z)) - logGamma(1 - z);
  z -= 1;
  let x = coef[0];
  for (let i = 1; i < g + 2; i++) x += coef[i] / (z + i);
  const t = z + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

export type WelchResult = { delta: number; se: number; t: number; df: number; p: number };

export function welch(withRow?: AggregateRow, withoutRow?: AggregateRow): WelchResult | null {
  if (!withRow || !withoutRow || withRow.n < 2 || withoutRow.n < 2) return null;
  const vw = withRow.sd ** 2 / withRow.n;
  const vo = withoutRow.sd ** 2 / withoutRow.n;
  const se = Math.sqrt(vw + vo);
  const delta = withRow.mean - withoutRow.mean;
  const t = delta / se;
  const df = (vw + vo) ** 2 / (vw ** 2 / (withRow.n - 1) + vo ** 2 / (withoutRow.n - 1));
  return { delta, se, t, df, p: tTwoTailedP(t, df) };
}

export type PairedResult = {
  n: number;
  mean: number;
  sd: number;
  se: number;
  t: number;
  p: number;
  positive: number;
};

export function pairedOfficers(rows: OfficerRow[], minEach = 1): PairedResult {
  const paired = rows.filter((o) => o.with_n >= minEach && o.without_n >= minEach);
  const n = paired.length;
  const mean = n ? paired.reduce((s, o) => s + o.delta, 0) / n : 0;
  const sd =
    n > 1 ? Math.sqrt(paired.reduce((s, o) => s + (o.delta - mean) ** 2, 0) / (n - 1)) : 0;
  const se = n > 0 ? sd / Math.sqrt(n) : 0;
  const t = se > 0 ? mean / se : 0;
  return {
    n,
    mean,
    sd,
    se,
    t,
    p: n > 1 ? tTwoTailedP(t, n - 1) : 1,
    positive: paired.filter((o) => o.delta > 0).length,
  };
}

export type MonthDelta = { month: string; withN: number; delta: number };

export function monthDeltas(rows: MonthRow[]): MonthDelta[] {
  const byMonth: Record<string, { with?: MonthRow; without?: MonthRow }> = {};
  for (const r of rows) (byMonth[r.month] ??= {})[r.has_checklist ? "with" : "without"] = r;
  return Object.keys(byMonth)
    .sort()
    .flatMap((month) => {
      const { with: w, without: o } = byMonth[month];
      return w && o ? [{ month, withN: w.n, delta: w.mean - o.mean }] : [];
    });
}

// Month-matched difference: each month's with-minus-without gap, weighted by
// that month's checklist cases. Removes drift in the score level over time.
export function monthMatchedDelta(rows: MonthRow[]): number {
  const deltas = monthDeltas(rows);
  const weight = deltas.reduce((s, d) => s + d.withN, 0);
  return weight ? deltas.reduce((s, d) => s + d.withN * d.delta, 0) / weight : 0;
}

// Mean score of a group across a set of months, weighted by case count.
export function groupMean(rows: MonthRow[], hasChecklist: boolean, test: (m: string) => boolean) {
  const sel = rows.filter((r) => r.has_checklist === hasChecklist && test(r.month));
  const n = sel.reduce((s, r) => s + r.n, 0);
  return { n, mean: n ? sel.reduce((s, r) => s + r.n * r.mean, 0) / n : 0 };
}

// Cases per group for 80% power at two-sided alpha = 0.05.
export function casesPerGroup(sd: number, effect: number): number {
  return Math.ceil((2 * (1.96 + 0.8416) ** 2 * sd ** 2) / effect ** 2);
}

export function formatP(p: number): string {
  if (p < 0.001) return "p < 0.001";
  return `p ≈ ${p < 0.01 ? p.toFixed(3) : p.toFixed(2)}`;
}

export type BucketShare = { label: string; with: number; without: number };

const BUCKETS = ["< 50", "50-64", "65-74", "75-84", "85-100"];

// Shares (percent, one decimal) of each group's cases per score band.
export function bucketShares(rows: DistributionRow[] | undefined): BucketShare[] {
  // Bundles from before the 2026-10-12 refresh carried no distribution rows;
  // these are the shares hand-computed from the 2026-04-22 snapshot.
  if (!rows?.length) {
    return [
      { label: "< 50", with: 7.4, without: 7.2 },
      { label: "65-74", with: 16.4, without: 17.6 },
      { label: "75-84", with: 60.7, without: 55.6 },
      { label: "85-100", with: 15.6, without: 19.6 },
    ];
  }
  const total = (has: boolean) =>
    rows.filter((r) => r.has_checklist === has).reduce((s, r) => s + r.n, 0) || 1;
  const share = (has: boolean, bucket: string) =>
    Math.round(
      (1000 * (rows.find((r) => r.has_checklist === has && r.bucket === bucket)?.n ?? 0)) /
        total(has)
    ) / 10;
  return BUCKETS.map((label) => ({ label, with: share(true, label), without: share(false, label) }));
}
